import React from 'react';
import { getItemsData, getSection, extractBaseIceCode } from './saleReportHelpers';

// รายงานลืมส่ง — customerId=FORGOT sentinel rows, own section so accounting
// can add one directly without going through the saleCode recode editor.
// Product picker reuses getItemsData(fullReport) (already loaded for the
// "สรุปยอดขาย" table below) instead of fetching a separate catalog.
// `forgotSalesRows` is the container's single FORGOT-sentinel filter
// (customerId === 'FORGOT'), passed down so this tab and SalesRowsTab can't
// drift apart on what counts as a forgot-sale row.
//
// Two modes (brief: forgot-sale-price, 2026-09-28):
// - "ขายปกติ": each item's unitPrice comes from the seller's DefaultPrice
//   (forgotPrices, fetched by the container from GET .../forgot-sale/prices).
//   Missing a price for any picked product blocks the save button — no
//   silent 0-price fallback, matching the tablet/backend.
// - "โปรโมชั่น": a single total price for the whole picked group, same as
//   the delivery-re app's promo sales (quick 100/200/ฟรี buttons).
const ForgotSaleTab = ({
    fullReport,
    forgotSalesRows,
    forgotSelectedIceCode,
    setForgotSelectedIceCode,
    forgotQtyInput,
    setForgotQtyInput,
    forgotPendingItems,
    addForgotPendingItem,
    removeForgotPendingItem,
    handleAddForgotSale,
    addingForgotSale,
    forgotMode,
    setForgotMode,
    forgotPromoPriceInput,
    setForgotPromoPriceInput,
    forgotPrices,
    loadingForgotPrices,
    forgotPricesError,
}) => {
    const master = getSection(fullReport, ['master', 'masterReport', 'report', 'masterData'], null);
    const isVerified = master?.verified === true;
    const forgotTotalQty = forgotSalesRows.reduce((sum, s) => sum + (Number(s.quantity) || 0), 0);
    // itemsBreakdown.items is one row PER BILL LINE (dozens for a real report), not
    // pre-aggregated per product, AND its iceCode embeds whatever price was in effect
    // at the time of that historical line (e.g. "1I35B" vs "1I40B" for the same product
    // sold at different prices over time). Group by the BASE code (forgotPrices' key
    // shape too) so "1I35B"/"1I40B" collapse into one "1I" option showing the seller's
    // CURRENT line price — not dozens of stale near-duplicates.
    const { items: rawItems } = getItemsData(fullReport);
    const productOptionsMap = new Map();
    rawItems.forEach(it => {
        const baseIceCode = extractBaseIceCode(it.iceCode);
        if (baseIceCode && !productOptionsMap.has(baseIceCode)) {
            productOptionsMap.set(baseIceCode, { ...it, baseIceCode });
        }
    });
    const productOptions = Array.from(productOptionsMap.values());

    const isPromo = forgotMode === 'promo';
    const pendingTotalQty = forgotPendingItems.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
    const missingPriceItems = isPromo ? [] : forgotPendingItems.filter(i => forgotPrices[i.baseIceCode] == null);
    const pendingNormalTotal = forgotPendingItems.reduce((sum, i) => {
        const price = forgotPrices[i.baseIceCode];
        return price == null ? sum : sum + price * i.quantity;
    }, 0);
    const promoPriceValid = forgotPromoPriceInput !== ''
        && Number.isInteger(Number(forgotPromoPriceInput))
        && Number(forgotPromoPriceInput) >= 0;
    const promoCodePreview = promoPriceValid && pendingTotalQty > 0
        ? `PR_${pendingTotalQty}P${Number(forgotPromoPriceInput)}B`
        : null;
    const saveDisabled = addingForgotSale
        || forgotPendingItems.length === 0
        || (!isPromo && missingPriceItems.length > 0)
        || (isPromo && !promoPriceValid);

    return (
        <div className="btc-section" style={{ gridColumn: 'span 2' }}>
            <h3 style={{ margin: '0 0 8px 0' }}>รายงานลืมส่ง{forgotSalesRows.length > 0 ? ` (${forgotTotalQty} ถุง สะสม)` : ''}</h3>
            {forgotSalesRows.length === 0 && <p style={{ color: '#6c757d' }}>ยังไม่มีรายการลืมส่งในรายงานนี้</p>}

            {forgotSalesRows.length > 0 && (
                <table className="btc-table" style={{ fontSize: '13px', marginBottom: '16px' }}>
                    <thead>
                        <tr>
                            <th>เวลา</th>
                            <th>รายการสินค้า</th>
                            <th>จำนวน (ถุง)</th>
                            <th>ยอด (฿)</th>
                            <th>ประเภท</th>
                        </tr>
                    </thead>
                    <tbody>
                        {forgotSalesRows.map((sale) => (
                            <tr key={sale.msdUid}>
                                <td>{sale.time ? String(sale.time).substring(0, 8) : ''}</td>
                                <td>
                                    {(sale.normalizedItems || sale.items || []).map((it, i) => (
                                        <span key={i} style={{ display: 'inline-block', marginRight: '8px' }}>
                                            {it.iceCode} x{it.quantity}
                                        </span>
                                    ))}
                                </td>
                                <td>{sale.quantity}</td>
                                <td>{Number(sale.total || 0).toLocaleString()}</td>
                                <td>
                                    {sale.promotion ? (
                                        <span style={{ background: '#ff8c00', color: '#fff', borderRadius: '6px', padding: '2px 8px', fontSize: '12px' }}>
                                            โปร
                                        </span>
                                    ) : (
                                        <span style={{ color: '#6c757d', fontSize: '12px' }}>ปกติ</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            {!isVerified && (
                <>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                        <button
                            className="btc-btn btn-sm"
                            style={{ backgroundColor: !isPromo ? '#007bff' : '#adb5bd' }}
                            onClick={() => setForgotMode('normal')}
                        >
                            ขายปกติ
                        </button>
                        <button
                            className="btc-btn btn-sm"
                            style={{ backgroundColor: isPromo ? '#ff8c00' : '#adb5bd' }}
                            onClick={() => setForgotMode('promo')}
                        >
                            โปรโมชั่น
                        </button>
                    </div>

                    {!isPromo && loadingForgotPrices && <p style={{ color: '#6c757d' }}>กำลังดึงราคาสาย...</p>}
                    {!isPromo && forgotPricesError && <p className="error" style={{ color: '#dc3545' }}>{forgotPricesError}</p>}

                    {forgotPendingItems.length > 0 && (
                        <ul style={{ margin: '0 0 8px 0', paddingLeft: '18px' }}>
                            {forgotPendingItems.map((item, i) => {
                                const price = forgotPrices[item.baseIceCode];
                                const noPrice = !isPromo && price == null;
                                return (
                                    <li key={i} style={{ fontSize: '13px', color: noPrice ? '#dc3545' : 'inherit' }}>
                                        {item.productName} x {item.quantity}
                                        {!isPromo && (noPrice
                                            ? ' — ยังไม่ได้ตั้งราคา!'
                                            : ` @ ${price.toLocaleString()} บาท = ${(price * item.quantity).toLocaleString()} บาท`)}
                                        {' '}
                                        <button
                                            className="btc-btn btn-sm"
                                            style={{ backgroundColor: '#dc3545', padding: '1px 6px', fontSize: '11px' }}
                                            onClick={() => removeForgotPendingItem(i)}
                                        >
                                            ลบ
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    {!isPromo && missingPriceItems.length > 0 && (
                        <p style={{ color: '#dc3545', fontWeight: 'bold' }}>
                            สายนี้ยังไม่ได้ตั้งราคาสินค้า: {missingPriceItems.map(i => i.productName || i.iceCode).join(', ')} — บันทึกไม่ได้จนกว่าจะตั้งราคา
                        </p>
                    )}
                    {!isPromo && forgotPendingItems.length > 0 && missingPriceItems.length === 0 && (
                        <p style={{ fontWeight: 'bold' }}>ยอดรวมก่อนกด: {pendingNormalTotal.toLocaleString()} บาท</p>
                    )}

                    {productOptions.length === 0 ? (
                        <p style={{ color: '#6c757d' }}>ยังไม่มีรายการสินค้าให้เลือก — ต้องมีรายการขายอย่างน้อย 1 รายการในรายงานนี้ก่อน</p>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <select
                                value={forgotSelectedIceCode}
                                onChange={e => setForgotSelectedIceCode(e.target.value)}
                                style={{ padding: '5px 8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                            >
                                <option value="">เลือกสินค้า</option>
                                {productOptions.map(p => (
                                    <option key={p.baseIceCode} value={p.baseIceCode}>
                                        {p.productName}
                                        {!isPromo && forgotPrices[p.baseIceCode] != null ? ` (${forgotPrices[p.baseIceCode]} บาท)` : ''}
                                        {!isPromo && forgotPrices[p.baseIceCode] == null ? ' (ยังไม่ตั้งราคา)' : ''}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="number"
                                min="1"
                                value={forgotQtyInput}
                                onChange={e => setForgotQtyInput(e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="จำนวน"
                                style={{ width: '100px', padding: '5px 8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                            />
                            <button
                                className="btc-btn btn-sm"
                                style={{ backgroundColor: '#6c757d' }}
                                onClick={() => addForgotPendingItem(productOptions)}
                            >
                                + เพิ่มรายการ
                            </button>
                        </div>
                    )}

                    {isPromo && (
                        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <label style={{ fontSize: '13px' }}>ราคารวมโปรโมชั่น (บาท):</label>
                            <input
                                type="number"
                                min="0"
                                value={forgotPromoPriceInput}
                                onChange={e => setForgotPromoPriceInput(e.target.value.replace(/[^0-9]/g, ''))}
                                placeholder="เช่น 100"
                                style={{ width: '100px', padding: '5px 8px', borderRadius: '4px', border: '1px solid #ced4da' }}
                            />
                            <button className="btc-btn btn-sm" style={{ backgroundColor: '#28a745' }} onClick={() => setForgotPromoPriceInput('100')}>100</button>
                            <button className="btc-btn btn-sm" style={{ backgroundColor: '#28a745' }} onClick={() => setForgotPromoPriceInput('200')}>200</button>
                            <button className="btc-btn btn-sm" style={{ backgroundColor: '#17a2b8' }} onClick={() => setForgotPromoPriceInput('0')}>ฟรี</button>
                            {promoCodePreview && (
                                <code style={{ fontSize: '12px', color: '#495057' }}>จะได้รหัสโปร: {promoCodePreview}</code>
                            )}
                        </div>
                    )}

                    {forgotPendingItems.length > 0 && (
                        <div style={{ marginTop: '10px' }}>
                            <button
                                className="btc-btn btn-sm"
                                style={{ backgroundColor: '#343a40' }}
                                onClick={handleAddForgotSale}
                                disabled={saveDisabled}
                            >
                                {addingForgotSale ? 'กำลังบันทึก...' : `บันทึกทั้งหมด (${forgotPendingItems.length} รายการ)`}
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ForgotSaleTab;
